import { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Search, MapPin, Star, TrendingUp, ShieldCheck, Tag, Layers, Phone,
  MessageSquare, Megaphone, Image, Clock, Globe,
} from "lucide-react";
import AuditForm from "@/components/audit/AuditForm";
import ScanningLoader from "@/components/audit/ScanningLoader";
import AuditResults from "@/components/audit/AuditResults";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HERO_IMG = "https://images.unsplash.com/photo-1592838064575-70ed626d3a0e";

const STATS = [
  { icon: Search, value: "3-Pack", label: "Own the map results that book jobs" },
  { icon: Star, value: "Reviews", label: "The #1 signal homeowners trust" },
  { icon: MapPin, value: "NAP", label: "One wrong digit sinks your ranking" },
  { icon: TrendingUp, value: "60 min", label: "From audit to a plan of attack" },
];

const CHECKS = [
  { icon: ShieldCheck, title: "Profile Health", desc: "Verification status and how complete your listing is." },
  { icon: Tag, title: "Business Name", desc: "Keyword-stuffing and spam checks Google penalizes." },
  { icon: Layers, title: "Categories", desc: "Primary and secondary categories that win moving searches." },
  { icon: Phone, title: "Phone & NAP", desc: "Name, address, phone consistency across the web." },
  { icon: Star, title: "Reviews & Rating", desc: "Volume, velocity and star rating vs. competitors." },
  { icon: MessageSquare, title: "Review Responses", desc: "How well you reply to reviews — a ranking factor." },
  { icon: Megaphone, title: "Google Posts", desc: "Post cadence that keeps your profile active and fresh." },
  { icon: Image, title: "Photos & Media", desc: "Photo count and freshness that build trust." },
  { icon: Globe, title: "Website & Hours", desc: "Landing page, business hours and service-area setup." },
];

const FAQS = [
  { q: "What is a Google Business Profile audit?", a: "A Google Business Profile (GBP) audit reviews every part of your listing — name, categories, NAP consistency, reviews, posts, photos, hours and more — and scores how well it's set up to rank in Google's local map pack and convert searchers into booked moves." },
  { q: "Is this GBP audit really free?", a: "Yes. The Google Business Profile audit is 100% free. Enter your business details and get an instant, competitor-grade report on screen plus a copy emailed to you — no credit card and no call required." },
  { q: "How long does the free audit take?", a: "The audit runs in about a minute. You'll see a live scan of your profile and then a full report with an overall health score, prioritized fixes and a breakdown of all 15 ranking factors." },
  { q: "What does the audit check?", a: "It checks profile health and verification, business name accuracy, primary and secondary categories, phone number, NAP consistency, service area, reviews and rating, review responses, Google Posts, photos, business hours, website link, description, services/attributes and Q&A." },
  { q: "Will this help my moving company rank higher on Google?", a: "The audit shows exactly where your Google Business Profile is losing visibility and gives you the specific fixes to climb the local map pack — the same playbook we use for moving companies at Movers Pro Marketing." },
];

export default function AuditPage() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [emailStatus, setEmailStatus] = useState(null);
  const [scanningName, setScanningName] = useState("");
  const topRef = useRef(null);

  const runAudit = async (form) => {
    setScanningName(form.business_name);
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/audit`, form);
      const auditId = data.id;
      const started = Date.now();
      let failures = 0;
      const poll = async () => {
        try {
          const { data: res } = await axios.get(`${API}/audit/${auditId}`);
          failures = 0;
          if (res.status === "done") {
            setResult({ id: res.id, audit: res.audit });
            setEmailStatus(res.email_status);
            setLoading(false);
            window.scrollTo({ top: 0, behavior: "smooth" });
            return;
          }
          if (res.status === "error") {
            throw new Error(res.error || "Audit failed");
          }
          if (Date.now() - started > 180000) {
            throw new Error("Audit is taking too long. Please try again.");
          }
          setTimeout(poll, 3000);
        } catch (err) {
          // Tolerate transient network/5xx errors while the job runs server-side.
          const recoverable = !err?.message?.startsWith("Audit");
          if (recoverable && failures < 4 && Date.now() - started < 180000) {
            failures += 1;
            setTimeout(poll, 4000);
            return;
          }
          console.error(err);
          toast.error(err?.response?.data?.detail || err.message || "Audit failed. Please try again.");
          setLoading(false);
        }
      };
      poll();
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || e.message || "Audit failed. Please try again.");
      setLoading(false);
    }
  };

  const reset = () => {
    setResult(null);
    setEmailStatus(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (result) {
    return <AuditResults result={result} emailStatus={emailStatus} onReset={reset} />;
  }

  return (
    <div ref={topRef} className="relative min-h-screen">
      {loading && <ScanningLoader businessName={scanningName} />}

      {/* Nav */}
      <header className="relative z-20 border-b-2 border-zinc-800 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/movers-pro-logo.webp" alt="Movers Pro Marketing — Free Google Business Profile Audit" className="h-7 sm:h-9 w-auto" data-testid="site-logo" />
          </div>
          <a
            href="https://calendly.com/moverspro-info/30min"
            target="_blank"
            rel="noopener noreferrer"
            className="font-heading text-xs sm:text-sm uppercase tracking-wider border-2 border-zinc-700 hover:border-brand text-white px-4 h-10 flex items-center transition-colors duration-150"
            data-testid="nav-book-call"
          >
            Book a call
          </a>
        </div>
      </header>

      {/* Hero + form */}
      <section className="relative noise-overlay">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMG})` }}
        />
        <div className="absolute inset-0 bg-background/85" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-16 lg:py-24 grid lg:grid-cols-2 gap-14 items-center">
          {/* Left */}
          <div className="fade-up">
            <span className="font-heading text-xs uppercase tracking-widest text-brand">
              Free Google Business Profile Audit
            </span>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl uppercase tracking-tighter leading-[0.9] text-white mt-4">
              If you're not on Google, you're{" "}
              <span className="text-brand">invisible</span> in your own city.
            </h1>
            <p className="font-body text-base sm:text-lg text-zinc-300 mt-6 max-w-lg">
              Enter your listing and get an instant, competitor-grade audit of your Google
              Business Profile — name, categories, NAP, reviews, posts, photos and everything
              that decides who gets the call.
            </p>

            <div className="grid grid-cols-2 gap-px bg-zinc-800 border-2 border-zinc-800 mt-10 max-w-lg">
              {STATS.map((s) => (
                <div key={s.value} className="bg-background p-5">
                  <s.icon className="h-5 w-5 text-brand mb-2" />
                  <div className="font-display text-2xl uppercase text-white leading-none">{s.value}</div>
                  <div className="font-body text-xs text-zinc-500 mt-1">{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right form */}
          <div className="fade-up" style={{ animationDelay: "0.15s" }}>
            <AuditForm onSubmit={runAudit} loading={loading} />
          </div>
        </div>
      </section>

      {/* SEO content: what the free GBP audit checks */}
      <section className="border-t-2 border-zinc-800 bg-background py-16 sm:py-20" data-testid="seo-checks">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <span className="font-heading text-xs uppercase tracking-widest text-brand">Free Google Business Profile Audit Tool</span>
          <h2 className="font-display text-4xl sm:text-5xl uppercase tracking-tight text-white leading-none mt-3 max-w-3xl">
            What our free GBP audit checks
          </h2>
          <p className="font-body text-base text-zinc-400 mt-4 max-w-2xl">
            Our free Google Business Profile audit grades your moving company's listing across every
            ranking and conversion signal Google uses to decide who shows up in the local map pack.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-800 border-2 border-zinc-800 mt-10">
            {CHECKS.map((c) => (
              <div key={c.title} className="bg-background p-6">
                <c.icon className="h-6 w-6 text-brand mb-3" />
                <h3 className="font-heading text-base uppercase tracking-wide text-white">{c.title}</h3>
                <p className="font-body text-sm text-zinc-500 mt-1">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SEO content: FAQ (matches FAQPage structured data) */}
      <section className="border-t-2 border-zinc-800 bg-secondary py-16 sm:py-20" data-testid="seo-faq">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <h2 className="font-display text-4xl sm:text-5xl uppercase tracking-tight text-white leading-none">
            Free GBP audit — FAQs
          </h2>
          <div className="mt-8 divide-y divide-zinc-800 border-2 border-zinc-800">
            {FAQS.map((f, i) => (
              <details key={i} className="group" data-testid={`faq-${i}`}>
                <summary className="flex items-center justify-between gap-4 cursor-pointer list-none p-5 hover:bg-background/50 transition-colors duration-150">
                  <h3 className="font-heading text-base sm:text-lg uppercase tracking-wide text-white">{f.q}</h3>
                  <span className="text-brand font-display text-2xl leading-none group-open:rotate-45 transition-transform duration-150">+</span>
                </summary>
                <p className="font-body text-sm sm:text-base text-zinc-400 px-5 pb-5">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Trust bar */}
      <section className="border-t-2 border-zinc-800 bg-secondary py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-wrap items-center justify-center gap-x-8 gap-y-2">
          <span className="font-heading text-xs uppercase tracking-widest text-zinc-500">
            Trusted by 50+ moving companies
          </span>
          {["IRONHAUL", "CITYLINE", "NORTHGATE", "REDLINE", "ATLAS VAN CO.", "SUMMIT"].map((n) => (
            <span key={n} className="font-heading text-sm uppercase tracking-wider text-zinc-600">{n}</span>
          ))}
        </div>
      </section>

      <footer className="bg-background py-8 text-center border-t-2 border-zinc-800">
        <p className="font-body text-xs text-zinc-600">Movers Pro Marketing · Movers first. Always.</p>
      </footer>
    </div>
  );
}
